export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem('token');
};

// The backend wraps responses in a data wrapper
interface DataWrapper<T> {
  data: T;
}

export interface PaginatedDataWrapper<T> {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  data: T;
}

export interface User {
  id: string;
  email: string;
  plan: PlanType;
  name?: string;
  pending_email?: string;
  created_at: string;
  updated_at?: string;
}

export type PlanType = 'free' | 'pro';

export type BillingPeriod = 'monthly' | 'yearly';

export interface BillingSession {
  url: string;
}

export interface LoginResponse {
  user: User;
  token: string;
}

export interface Question {
  id: string;
  title: string;
  description?: string;
  is_public: boolean;
  share_slug?: string;
  created_by: string;
  created_at: string;
  dependent_variables?: Variable[];
  independent_variables?: Variable[];
}

export interface Variable {
  id: string;
  name: string;
  description?: string;
  type: 'numeric' | 'categorical' | 'boolean';
  dependent: boolean;
  created_by: string;
  created_at: string;
}

export interface UserLite {
  id: string;
  name?: string;
  created_at: string;
}

export interface DataPointValue {
  value: number | string | boolean | null;
  description?: string;
}

export interface DataPoint {
  id: string;
  created_by: UserLite;
  created_at: string;
  values: Record<string, DataPointValue>;
}

export interface Collaborator {
  user_id: string;
  name?: string;
  email: string;
  role: string;
  created_at: string;
}

export interface Invitation {
  token: string;
  question_id: string;
  question_title: string;
  created_at: string;
  expires_at: string;
  accepted_at?: string;
}

export interface CorrelationResult {
  method: string;
  effect_size: number;
  p_value: number;
  sample_size: number;
  error?: string;
}

export interface Dataset {
  variables: Record<string, Variable>;
  datapoints: DataPoint[];
  correlations?: Record<string, Record<string, CorrelationResult>>;
}

export interface SharedQuestion {
  title: string;
  description?: string;
  owner_name?: string;
  created_at: string;
  dataset: Dataset;
  total_datapoints: number;
}

class APIClient {
  private token: string | null = getStoredToken();

  // Invoked when an authenticated request comes back 401, i.e. the stored
  // session token has expired or was revoked server-side.
  private onSessionExpired: (() => void) | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  setOnSessionExpired(handler: (() => void) | null) {
    this.onSessionExpired = handler;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private async handleError(
    response: Response,
    fallbackMessage: string,
    opts: { usedAuth?: boolean } = {},
  ): Promise<never> {
    // A 401 on a request that carried our bearer token means the session is
    // no longer valid server-side. Drop it and notify the app so it stops
    // presenting itself as logged in. Endpoints that never send the token
    // (login, signup, ...) pass usedAuth: false; their 401s mean bad
    // credentials, not an expired session.
    if (opts.usedAuth !== false && response.status === 401 && this.token) {
      this.token = null;
      this.onSessionExpired?.();
    }

    let serverMessage: string | null = null;
    
    try {
      const errorData = await response.json();
      serverMessage = errorData.error || errorData.message || null;
    } catch (parseError) {
      // If we can't parse the response, just use the fallback
    }

    if (serverMessage) {
      throw new Error(`${fallbackMessage}: ${serverMessage}`);
    } else {
      throw new Error(fallbackMessage);
    }
  }

  // Authentication
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/v1/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      await this.handleError(response, 'Login failed', { usedAuth: false });
    }

    const wrapper: DataWrapper<LoginResponse> = await response.json();
    return wrapper.data;
  }

  async getUser(userId: string): Promise<UserLite> {
    const response = await fetch(`${API_BASE_URL}/v1/users/${userId}`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch user');
    const wrapper: DataWrapper<UserLite> = await response.json();
    return wrapper.data;
  }

  async getSelfUser(): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/v1/me`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch profile');
    const wrapper: DataWrapper<User> = await response.json();
    return wrapper.data;
  }

  async updateUser(userId: string, data: {
    name?: string;
    email?: string;
    password?: string;
  }): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/v1/users/${userId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to update user');
    const wrapper: DataWrapper<User> = await response.json();
    return wrapper.data;
  }

  async deleteUser(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/users/${userId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to delete account');
  }

  async createUser(email: string, password: string, name?: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/v1/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });

    if (!response.ok) {
      await this.handleError(response, 'Failed to create user', { usedAuth: false });
    }

    const wrapper: DataWrapper<User> = await response.json();
    return wrapper.data;
  }

  async verifyEmail(token: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/actions/users/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      await this.handleError(response, 'Failed to verify email', { usedAuth: false });
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/actions/users/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      await this.handleError(response, 'Failed to request password reset', { usedAuth: false });
    }
  }

  async resetPassword(token: string, password: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/actions/users/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });

    if (!response.ok) {
      await this.handleError(response, 'Failed to reset password', { usedAuth: false });
    }
  }

  async resendVerificationEmail(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/actions/users/${userId}/resend-verification-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      await this.handleError(response, 'Failed to resend verification email', { usedAuth: false });
    }
  }

  // Billing
  async createBillingCheckoutSession(period: BillingPeriod = 'monthly'): Promise<BillingSession> {
    const response = await fetch(`${API_BASE_URL}/v1/billing/checkout-session`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        plan: 'pro',
        billing_period: period,
      }),
    });
    if (!response.ok) await this.handleError(response, 'Failed to start checkout');
    const wrapper: DataWrapper<BillingSession> = await response.json();
    return wrapper.data;
  }

  async createBillingPortalSession(): Promise<BillingSession> {
    const response = await fetch(`${API_BASE_URL}/v1/billing/portal-session`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({}),
    });
    if (!response.ok) await this.handleError(response, 'Failed to open billing portal');
    const wrapper: DataWrapper<BillingSession> = await response.json();
    return wrapper.data;
  }

  // Questions
  async listQuestions(): Promise<Question[]> {
    const response = await fetch(`${API_BASE_URL}/v1/questions`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch questions');
    const wrapper: DataWrapper<Question[]> = await response.json();
    return wrapper.data;
  }

  async getQuestion(id: string): Promise<Question> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${id}`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch question');
    const wrapper: DataWrapper<Question> = await response.json();
    return wrapper.data;
  }

  async createQuestion(data: {
    title: string;
    description?: string;
    is_public: boolean;
  }): Promise<Question> {
    const response = await fetch(`${API_BASE_URL}/v1/questions`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to create question');
    const wrapper: DataWrapper<Question> = await response.json();
    return wrapper.data;
  }

  async updateQuestion(id: string, data: {
    title?: string;
    description?: string;
    is_public?: boolean;
  }): Promise<Question> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${id}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to update question');
    const wrapper: DataWrapper<Question> = await response.json();
    return wrapper.data;
  }

  // Public sharing
  async shareQuestion(id: string): Promise<Question> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${id}/share`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to share question');
    const wrapper: DataWrapper<Question> = await response.json();
    return wrapper.data;
  }

  async unshareQuestion(id: string): Promise<Question> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${id}/share`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to stop sharing question');
    const wrapper: DataWrapper<Question> = await response.json();
    return wrapper.data;
  }

  // Public endpoint; sends no credentials.
  async getSharedQuestion(slug: string): Promise<SharedQuestion> {
    const response = await fetch(`${API_BASE_URL}/v1/public/questions/${slug}`);
    if (!response.ok) await this.handleError(response, 'This page is not available');
    const wrapper: DataWrapper<SharedQuestion> = await response.json();
    return wrapper.data;
  }

  // Collaborators
  async listCollaborators(questionId: string): Promise<Collaborator[]> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/collaborators`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch collaborators');
    const outer: DataWrapper<{ data: Collaborator[] }> = await response.json();
    return outer.data.data;
  }

  async removeCollaborator(questionId: string, userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/collaborators/${userId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to remove collaborator');
  }

  // Invitations
  async createInvitation(questionId: string): Promise<Invitation> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/invitations`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to create invitation');
    const outer: DataWrapper<Invitation> = await response.json();
    return outer.data;
  }

  async listInvitations(questionId: string): Promise<Invitation[]> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/invitations`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch invitations');
    const outer: DataWrapper<{ data: Invitation[] }> = await response.json();
    return outer.data.data;
  }

  async revokeInvitation(questionId: string, token: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/invitations/${token}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to revoke invitation');
  }

  async getInvitation(token: string): Promise<Invitation> {
    const response = await fetch(`${API_BASE_URL}/v1/invitations/${token}`);
    if (!response.ok) await this.handleError(response, 'Invitation not found or expired', { usedAuth: false });
    const outer: DataWrapper<Invitation> = await response.json();
    return outer.data;
  }

  async acceptInvitation(token: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/invitations/${token}/accept`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to accept invitation');
  }

  async deleteQuestion(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to delete question');
  }

  // Variables
  async listVariables(questionId: string): Promise<Variable[]> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/variables`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch variables');
    const wrapper: DataWrapper<Variable[]> = await response.json();
    return wrapper.data;
  }

  async createVariable(questionId: string, data: {
    name: string;
    description?: string;
    type: 'numeric' | 'categorical' | 'boolean';
    dependent: boolean;
  }): Promise<Variable> {
    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/variables`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to create variable');
    const wrapper: DataWrapper<Variable> = await response.json();
    return wrapper.data;
  }

  async updateVariable(variableId: string, data: {
    name?: string;
    description?: string;
    dependent?: boolean;
  }): Promise<Variable> {
    const response = await fetch(`${API_BASE_URL}/v1/variables/${variableId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to update variable');
    const wrapper: DataWrapper<Variable> = await response.json();
    return wrapper.data;
  }

  async deleteVariable(variableId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/variables/${variableId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to delete variable');
  }

  async listCategoricalValues(variableId: string): Promise<string[]> {
    const response = await fetch(`${API_BASE_URL}/v1/variables/${variableId}/categorical-values`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch categorical values');
    const wrapper: { data: string[] } = await response.json();
    return wrapper.data;
  }

  // Dataset (datapoints, variables, correlations)
  async listDataset(
    questionId: string,
    page?: number,
    perPage?: number
  ): Promise<PaginatedDataWrapper<Dataset>> {
    const params = new URLSearchParams();
    if (page !== undefined) {
      params.set('page', String(page));
    }
    if (perPage !== undefined) {
      params.set('per_page', String(perPage));
    }
    const query = params.toString();
    const url = query.length > 0
      ? `${API_BASE_URL}/v1/questions/${questionId}/datapoints?${query}`
      : `${API_BASE_URL}/v1/questions/${questionId}/datapoints`;

    const response = await fetch(url, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to fetch dataset');
    const wrapper: PaginatedDataWrapper<Dataset> = await response.json();
    return wrapper;
  }

  async createDataPoint(
    questionId: string,
    values: Record<string, DataPointValue>,
    recordedAt: string
  ): Promise<DataPoint> {
    const body = {
      values,
      recorded_at: recordedAt,
    };

    const response = await fetch(`${API_BASE_URL}/v1/questions/${questionId}/datapoints`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) await this.handleError(response, 'Failed to create data point');
    const wrapper: DataWrapper<DataPoint> = await response.json();
    return wrapper.data;
  }

  async updateDataPoint(
    datapointId: string,
    data: {
      values?: Record<string, DataPointValue>;
      recorded_at?: string;
    }
  ): Promise<DataPoint> {
    const response = await fetch(`${API_BASE_URL}/v1/datapoints/${datapointId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) await this.handleError(response, 'Failed to update data point');
    const wrapper: DataWrapper<DataPoint> = await response.json();
    return wrapper.data;
  }

  async deleteDataPoint(datapointId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/datapoints/${datapointId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to delete data point');
  }

  async exportDataset(
    questionId: string,
    format: 'csv' | 'json',
    includeDescriptions: boolean,
  ): Promise<void> {
    const params = new URLSearchParams({ question_id: questionId, format, include_descriptions: String(includeDescriptions) });
    const response = await fetch(`${API_BASE_URL}/v1/datapoints/export?${params}`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleError(response, 'Failed to export dataset');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  }

}

export const api = new APIClient();
